import { Component, OnInit, Input } from '@angular/core';

// https://github.com/SMasiu/LCD-display
/*
  number - Number to display. Default 0
  backgroundColor - Display's background-color. Default #000 (black)
  width - Digit width. Default 20px
  height - Digit height. Default 40px
  activeColor - Active lines color. Default rgb(243, 0, 0) (bright-red)
  disabledColor - Disabled lines color. Default rgb(60, 0, 0) (dark-red)
  maxNumberLength - Maximal number of digits in number. Each number with more digits then maxNumberLength'll be writen like 9*maxNumberLength for example: maxNumberLength = 3 => 999 Setting this value to null this option'll be disactiated. Default null
  minNumberLength - Minimal number of digits in number. Each number with less digits then minNumberLength'll be writen for example: minNumberLength = 4; number = 21 => 0021 Setting this value to null this option'll be disactiated .Default null
*/

export type LcdDisplayCell = { kind: 'digit'; char: string; decimalAfter?: boolean };

/** Formats a weight for LCD: comma only when there is a fractional part (0.5 kg steps). */
export function formatWeightForLcd(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  if (!Number.isFinite(rounded)) {
    return '0';
  }
  const hasFraction = Math.abs(rounded - Math.round(rounded)) >= 0.01;
  if (hasFraction) {
    return rounded.toFixed(1).replace('.', ',');
  }
  return String(Math.round(rounded));
}

/** Builds digit cells and optional decimal separator for the 7-segment display. */
export function buildLcdDisplayCells(
  value: number,
  maxDigits: number | null = null,
  minDigits: number | null = null,
): LcdDisplayCell[] {
  const text = formatWeightForLcd(value);
  let digits: string[] = [];
  let decimalAfter: number | null = null;

  for (const ch of text) {
    if (ch === ',') {
      if (digits.length > 0) {
        decimalAfter = digits.length - 1;
      }
      continue;
    }
    if (/[0-9]/.test(ch)) {
      digits.push(ch);
    }
  }

  if (digits.length === 0) {
    digits = ['0'];
    decimalAfter = null;
  }

  if (minDigits != null && digits.length < minDigits) {
    const pad = minDigits - digits.length;
    digits = [...Array(pad).fill('0'), ...digits];
    if (decimalAfter != null) {
      decimalAfter += pad;
    }
  }

  if (maxDigits != null && digits.length > maxDigits) {
    digits = Array(maxDigits).fill('9');
    decimalAfter = null;
  }

  const cells: LcdDisplayCell[] = [];
  for (let i = 0; i < digits.length; i++) {
    cells.push({
      kind: 'digit',
      char: digits[i],
      decimalAfter: decimalAfter === i,
    });
  }
  return cells;
}

@Component({
    selector: 'app-lcd-display',
    templateUrl: './lcd-display.component.html',
    styleUrls: ['./lcd-display.component.css'],
    standalone: false
})
export class LcdDisplayComponent implements OnInit {

  _number: number = 0;

  @Input() set number(v) {
    this._number = v;
    this.showNumber();
  }

  @Input() backgroundColor: string = '#000';
  @Input() width: string = '20px';
  @Input() height: string = '40px';
  @Input() activeColor: string = 'rgb(243, 0, 0)';
  @Input() disabledColor: string = 'rgb(60, 0, 0)';
  @Input() maxNumberLength: number | null = null;
  @Input() minNumberLength: number | null = null;

  reg1 = /[1,4]/;
  reg2 = /[1-3,7]/;
  reg3 = /[5,6]/;
  reg4 = /[0,1,7]/;
  reg5 = /[1,3-5,7,9]/;
  reg6 = /[2]/;
  reg7 = /[1,4,7]/;

  displayCells: LcdDisplayCell[] = [];

  constructor() { }

  showNumber(): void {
    if (this.minNumberLength != null && this.maxNumberLength != null && this.minNumberLength > this.maxNumberLength) {
      throw new Error("min number can't be higher than max");
    }
    this.displayCells = buildLcdDisplayCells(
      Number(this._number),
      this.maxNumberLength,
      this.minNumberLength,
    );
  }

  ngOnInit(): void {
    this.showNumber();
  }

}
